import { forwardRef } from 'react'

import {
  isValidBankAccountNumberAus,
  isValidBankAccountNumberCad,
  isValidBankAccountNumberGb,
  isValidBankAccountNumberUsa,
} from '../../utils/validation'
import { ArcadeHelpTextComponentProps } from '../Arcade/Input/ArcadeHelpText'
import FormattedInput, { BaseFormattedInputProps } from '../Arcade/Input/FormattedInput'

export type AccountNumberRegion = 'US' | 'CA' | 'GB' | 'AU'

export interface AccountNumberInputProps extends BaseFormattedInputProps, ArcadeHelpTextComponentProps {
  /** The region that determines format and validation */
  region: AccountNumberRegion
}

export const accountNumberRegionConfig = {
  AU: {
    format: '#'.repeat(10),
    validate: isValidBankAccountNumberAus,
  },
  CA: {
    format: '#'.repeat(12),
    validate: isValidBankAccountNumberCad,
  },
  GB: {
    format: '#'.repeat(9),
    validate: isValidBankAccountNumberGb,
  },
  US: {
    format: '#'.repeat(17),
    validate: isValidBankAccountNumberUsa,
  },
} as const

/**
 * A formatted input for bank account numbers with region-specific formatting and validation.
 * Supports US, GB, and AU regions with appropriate format templates.
 *
 * @example
 * ```tsx
 * <FieldLabel label="Account Number">
 *   <AccountNumberInput
 *     region="US"
 *     value={value}
 *     onChange={setValue}
 *     placeholder="Enter your account number"
 *   />
 * </FieldLabel>
 * ```
 */
const AccountNumberInput = forwardRef<HTMLInputElement, AccountNumberInputProps>(
  ({ region, placeholder, ...rest }, ref) => {
    const { format } = accountNumberRegionConfig[region]

    return <FormattedInput ref={ref} template={format} placeholder={placeholder || format} {...rest} />
  }
)

export default AccountNumberInput
